import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import SessionList from './SessionList';
import { useAuth } from '../../contexts/AuthContext';
import {
  House,
  ChatCircle,
  MagnifyingGlass,
  Plus,
  Gear,
  SignOut,
  CaretDown,
  List,
  X,
} from '@phosphor-icons/react';

const Sidebar = ({
  sessions,
  currentSessionId,
  onNewChat,
  onSelectSession,
  onDeleteSession,
  onClearAll
}) => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const userMenuRef = useRef(null);
  const sidebarRef = useRef(null);

  const isStudent = currentUser?.user_type === 'student';
  const isTeacher = currentUser?.user_type === 'teacher';

  const handleLogout = async () => {
    try {
      await logout();
      setIsUserMenuOpen(false);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  // Handle clicks outside the user menu to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle clicks outside the sidebar on mobile to close it
  useEffect(() => {
    const handleClickOutsideSidebar = (event) => {
      if (sidebarRef.current &&
          !sidebarRef.current.contains(event.target) &&
          !event.target.classList.contains('mobile-menu-toggle')) {
        setIsMobileMenuOpen(false);
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutsideSidebar);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutsideSidebar);
    };
  }, [isMobileMenuOpen]);

  // Handle ESC key to close mobile menu
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        setIsMobileMenuOpen(false);
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isMobileMenuOpen]);

  const handleSettingsClick = () => {
    if (isStudent) {
      navigate('/student/settings');
    } else if (isTeacher) {
      navigate('/teacher/settings');
    }
    setIsUserMenuOpen(false);
    setIsMobileMenuOpen(false);
  };

  const handleSessionClick = (sessionId) => {
    onSelectSession(sessionId);
    // Close mobile menu after selecting a session on mobile
    setIsMobileMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <>
      {/* Mobile menu button - only visible on mobile */}
      <button
        className="mobile-menu-toggle md:hidden fixed top-4 left-4 z-50 p-2 bg-indigo-600 text-white rounded-lg shadow-md"
        onClick={toggleMobileMenu}
        aria-label="Toggle menu"
        style={{ top: '16px', left: '16px' }}
      >
        {isMobileMenuOpen ? <X size={20} /> : <List size={20} />}
      </button>

      {/* Sidebar */}
      <div
        ref={sidebarRef}
        id="sidebar"
        className={`
          md:w-[280px] md:min-w-[280px] bg-gradient-to-b from-white to-gray-50/50 border-r border-gray-200 flex flex-col
          fixed md:static inset-0 z-40 transition-transform duration-300 ease-in-out
          ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
          w-[85%] max-w-[320px] h-full
        `}
      >
        {/* Sidebar Header - single position with title and mobile close button */}
        <div className="sidebar-header p-4 border-b border-gray-200 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <img
              src="/assets/images/brillabee logo.png"
              alt="Brillabee Logo"
              className="h-8 w-8 object-contain"
            />
            <h2 className="text-xl font-bold text-gray-800">Brillabee</h2>
          </div>
          {/* Mobile close button that actually works */}
          {isMobileMenuOpen && (
            <button
              className="md:hidden text-gray-500 hover:text-gray-700"
              onClick={() => setIsMobileMenuOpen(false)}
              aria-label="Close menu"
            >
              <X size={20} />
            </button>
          )}
        </div>

        <div className="sidebar-actions p-4 space-y-2">
          <div className="flex items-center space-x-2">
            <button
              id="new-chat-button"
              className="new-chat-btn flex items-center py-2 px-4 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700 shadow-sm transition-all"
              onClick={() => {
                onNewChat();
                setIsMobileMenuOpen(false);
              }}
              aria-label="New chat"
            >
              <Plus size={16} weight="bold" className="mr-2" />
              New chat
            </button>
            <button
              className={`search-btn p-2 rounded-md text-gray-600 transition-colors ${searchOpen ? 'bg-indigo-100 text-indigo-600' : 'bg-gray-100 hover:bg-gray-200'}`}
              onClick={() => {
                setSearchOpen(!searchOpen);
                if (searchOpen) setSearchQuery('');
              }}
              aria-label="Search conversations"
            >
              <MagnifyingGlass size={16} />
            </button>
          </div>
          {searchOpen && (
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search conversations..."
              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:border-indigo-500"
              autoFocus
              aria-label="Search conversations"
            />
          )}
        </div>

        {/* Student navigation links */}
        {isStudent && (
          <div className="px-4 py-2 border-b border-gray-200 flex flex-col gap-1">
            <button
              onClick={() => {
                navigate('/student/dashboard');
                setIsMobileMenuOpen(false);
              }}
              className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                location.pathname === '/student/dashboard'
                  ? 'bg-indigo-50 text-indigo-700 border-l-[3px] border-l-indigo-600'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
              aria-label="My Dashboard"
            >
              <House size={16} weight={location.pathname === '/student/dashboard' ? 'fill' : 'regular'} />
              My Dashboard
            </button>
            <button
              onClick={() => {
                onNewChat();
                navigate('/student/chat');
                setIsMobileMenuOpen(false);
              }}
              className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                location.pathname === '/student/chat'
                  ? 'bg-indigo-50 text-indigo-700 border-l-[3px] border-l-indigo-600'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
              aria-label="Start Learning"
            >
              <ChatCircle size={16} weight={location.pathname === '/student/chat' ? 'fill' : 'regular'} />
              Start Learning
            </button>
          </div>
        )}

        <div className="conversations-header p-4 border-b border-gray-200 flex justify-between items-center">
          <span className="text-sm font-medium text-gray-700">Your conversations</span>
          <button
            className="clear-all-btn text-xs text-gray-500 hover:text-red-500"
            onClick={() => {
              if (window.confirm('Delete all conversations? This cannot be undone.')) {
                onClearAll();
              }
            }}
            aria-label="Clear all conversations"
          >
            Clear All
          </button>
        </div>

        <SessionList
          sessions={searchQuery
            ? sessions.filter(s => (s.title || '').toLowerCase().includes(searchQuery.toLowerCase()))
            : sessions
          }
          currentSessionId={currentSessionId}
          onSelectSession={handleSessionClick}
          onDeleteSession={onDeleteSession}
        />

        <div className="sidebar-footer relative mt-auto p-4 border-t border-gray-200" ref={userMenuRef}>
          {isUserMenuOpen && (
            <div className="absolute bottom-16 left-0 mb-2 w-[calc(100%-2rem)] ml-4 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 py-1 z-50">
              <div className="px-3 py-2 border-b border-gray-100">
                  <p className="text-sm font-medium text-gray-800 truncate">{currentUser?.username || 'User'}</p>
                  {currentUser?.email && <p className="text-xs text-gray-500 truncate">{currentUser.email}</p>}
              </div>
              <button
                onClick={handleSettingsClick}
                className="w-full text-left flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                <Gear size={16} className="mr-2.5 text-gray-500" />
                Settings
              </button>
              <button
                onClick={handleLogout}
                className="w-full text-left flex items-center px-3 py-2 text-sm text-red-600 hover:bg-red-50"
              >
                <SignOut size={16} className="mr-2.5 text-red-500" />
                Logout
              </button>
            </div>
          )}

          <button
            onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
            className="user-btn w-full flex items-center justify-between py-2 px-3 rounded-md text-gray-700 text-sm font-medium hover:bg-gray-100"
          >
            <div className="flex items-center">
              <div className="user-avatar w-8 h-8 rounded-full bg-indigo-600 text-white flex items-center justify-center text-xs font-bold mr-2 ring-2 ring-indigo-200">
                {(currentUser?.username || 'S').charAt(0).toUpperCase()}
              </div>
              <span className="user-name truncate">{currentUser?.username || 'Student'}</span>
            </div>
            <CaretDown
              size={12}
              className={`text-gray-500 transform transition-transform ${isUserMenuOpen ? 'rotate-180' : ''}`}
            />
          </button>
        </div>
      </div>

      {/* Overlay for mobile - only visible when mobile menu is open */}
      {isMobileMenuOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-30"
          onClick={() => setIsMobileMenuOpen(false)}
        ></div>
      )}
    </>
  );
};

export default Sidebar;
